import {
  ROTULO_DO_STATUS_DE_SINISTRO,
  ROTULO_DO_TIPO_DE_SINISTRO,
  STATUS_DE_SINISTRO,
  type StatusDeSinistro,
  TIPOS_DE_SINISTRO,
  type TipoDeSinistro,
  formatarPlaca,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CircleCheck,
  EllipsisVertical,
  type LucideIcon,
  Pencil,
  Plus,
  Shield,
  ShieldAlert,
  TriangleAlert,
  Wrench,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { identificacaoDoVeiculo } from '@/features/frota/api'
import { referenciaDaReserva, useFrotaParaSelecao } from '@/features/multas/api'
import { type Sinistro, useSinistros } from '@/features/sinistros/api'
import { DialogoDeSinistro } from '@/features/sinistros/dialogo-de-sinistro'
import { SEM_VALOR, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'

/**
 * Sinistros — Adendo D.
 *
 * ---------------------------------------------------------------------------
 * A COLUNA QUE O FINANCEIRO PROCURA É A RESPONSABILIDADE
 * ---------------------------------------------------------------------------
 * `responsabilidadeDoCliente` é o interruptor que autoriza a franquia a virar
 * linha de cobrança no fechamento — por isso a coluna diz "Do cliente" ou
 * "Não atribuída" por extenso, nunca um checkbox mudo: a AUSÊNCIA de
 * responsabilidade também é uma decisão registrada.
 *
 * O amarelo aparece uma vez, no CTA "Registrar sinistro". `ABERTO` é âmbar
 * semântico (exige ação), nunca a cor da marca.
 */

const TAMANHO_DA_PAGINA = 20

/**
 * Cor, ícone e rótulo no MESMO registro — cor nunca comunica sozinha. `ABERTO`
 * é o único âmbar da tabela: é o estado que exige ação (acionar seguradora,
 * apurar). `ENCERRADO` é sucesso porque o processo terminou; `EM_REPARO` é
 * neutro — oficina trabalhando é o curso normal, não um alerta.
 */
const APRESENTACAO_DO_STATUS = {
  ABERTO: { icone: TriangleAlert, variante: 'warning' },
  SEGURADORA_ACIONADA: { icone: Shield, variante: 'info' },
  EM_REPARO: { icone: Wrench, variante: 'neutral' },
  ENCERRADO: { icone: CircleCheck, variante: 'success' },
} as const satisfies Readonly<
  Record<StatusDeSinistro, { readonly icone: LucideIcon; readonly variante: BadgeProps['variant'] }>
>

function BadgeDeStatusDoSinistro({ status }: { readonly status: StatusDeSinistro }): ReactElement {
  const { icone: Icone, variante } = APRESENTACAO_DO_STATUS[status]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_SINISTRO[status]}
    </Badge>
  )
}

export function SinistrosPage(): ReactElement {
  const podeCriar = usePermissao('incident.create')
  const podeAtualizar = usePermissao('incident.update')

  const [pagina, setPagina] = useState(1)
  const [status, setStatus] = useState<StatusDeSinistro | ''>('')
  const [tipo, setTipo] = useState<TipoDeSinistro | ''>('')
  const [vehicleId, setVehicleId] = useState('')

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Sinistro | undefined>(undefined)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, status, tipo, vehicleId }),
    [pagina, status, tipo, vehicleId],
  )

  const consulta = useSinistros(filtros)
  const frota = useFrotaParaSelecao()

  const temFiltro = status !== '' || tipo !== '' || vehicleId !== ''

  function limparFiltros(): void {
    setStatus('')
    setTipo('')
    setVehicleId('')
    setPagina(1)
  }

  function abrirRegistro(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(sinistro: Sinistro): void {
    setEmEdicao(sinistro)
    setDialogoAberto(true)
  }

  /** Modelo por veículo — a resposta traz a placa, quem lê pensa em carro. */
  const modeloPorVeiculo = useMemo(() => {
    const mapa = new Map<string, string>()

    for (const veiculo of frota.data?.itens ?? []) {
      mapa.set(veiculo.id, veiculo.modelo)
    }

    return mapa
  }, [frota.data])

  const colunas = useMemo<ColumnDef<Sinistro>[]>(
    () => [
      {
        id: 'data',
        header: 'Data',
        accessorFn: (linha: Sinistro) => linha.dataDoSinistro,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
            {formatarDataHora(row.original.dataDoSinistro)}
          </span>
        ),
      },
      {
        id: 'tipo',
        header: 'Tipo',
        accessorFn: (linha: Sinistro) => linha.tipo,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-medium text-foreground">
            {ROTULO_DO_TIPO_DE_SINISTRO[row.original.tipo]}
          </span>
        ),
      },
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha: Sinistro) => linha.veiculoPlaca,
        cell: ({ row }) => {
          const sinistro: Sinistro = row.original
          const modelo = modeloPorVeiculo.get(sinistro.vehicleId)

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap font-medium tabular-money text-foreground">
                {formatarPlaca(sinistro.veiculoPlaca)}
              </p>
              {modelo === undefined ? null : (
                <p className="truncate text-xs text-fg-secondary">{modelo}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'bo',
        header: 'BO',
        enableSorting: false,
        cell: ({ row }) => {
          const bo = row.original.boletimDeOcorrencia

          return bo === null ? (
            <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          ) : (
            <span className="whitespace-nowrap text-sm tabular-money text-foreground">{bo}</span>
          )
        },
      },
      {
        id: 'seguradora',
        header: 'Seguradora',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.seguradoraAcionada ? (
            <span className="whitespace-nowrap text-sm text-foreground">Acionada</span>
          ) : (
            <span className="whitespace-nowrap text-sm text-fg-muted">Não acionada</span>
          ),
      },
      {
        id: 'franquia',
        header: 'Franquia',
        meta: { alinhamento: 'direita' },
        // String de centavos: ordenar lexicograficamente mentiria, e number
        // não toca em dinheiro.
        enableSorting: false,
        cell: ({ row }) => (
          <span className="money-cell block min-w-24 text-foreground">
            {formatarMoedaDoWire(row.original.franquiaDaApolice)}
          </span>
        ),
      },
      {
        id: 'responsabilidade',
        header: 'Responsabilidade',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.responsabilidadeDoCliente ? (
            <span className="whitespace-nowrap text-sm font-medium text-foreground">
              Do cliente
            </span>
          ) : (
            <span className="whitespace-nowrap text-sm text-fg-muted">Não atribuída</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeStatusDoSinistro status={row.original.status} />,
      },
      {
        id: 'reserva',
        header: 'Reserva',
        enableSorting: false,
        cell: ({ row }) => {
          const reservationId = row.original.reservationId

          if (reservationId === null) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <span
              className="whitespace-nowrap text-sm tabular-money text-foreground"
              title={reservationId}
            >
              {referenciaDaReserva(reservationId)}
            </span>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const sinistro: Sinistro = row.original

          if (!podeAtualizar) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações do sinistro de ${formatarPlaca(sinistro.veiculoPlaca)} em ${formatarDataHora(sinistro.dataDoSinistro)}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    abrirEdicao(sinistro)
                  }}
                >
                  <Pencil aria-hidden="true" />
                  Atualizar andamento
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // O que muda o conjunto de colunas são a permissão e o mapa de modelos.
    [podeAtualizar, modeloPorVeiculo],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Sinistros</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            Colisão, roubo, furto e o resto do que não deveria acontecer. A responsabilidade do
            cliente é o que autoriza a franquia a entrar no fechamento da reserva.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirRegistro}>
            <Plus aria-hidden="true" />
            Registrar sinistro
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-sinistros">Status</Label>
          <Select
            id="filtro-status-sinistros"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_SINISTRO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {STATUS_DE_SINISTRO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS_DE_SINISTRO[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-tipo-sinistros">Tipo</Label>
          <Select
            id="filtro-tipo-sinistros"
            value={tipo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setTipo(TIPOS_DE_SINISTRO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {TIPOS_DE_SINISTRO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_TIPO_DE_SINISTRO[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-veiculo-sinistros">Veículo</Label>
          <Select
            id="filtro-veiculo-sinistros"
            value={vehicleId}
            onChange={(evento) => {
              setPagina(1)
              setVehicleId(evento.target.value)
            }}
            disabled={frota.isPending}
          >
            <option value="">
              {frota.isPending ? 'Carregando a frota…' : 'Todos os veículos'}
            </option>
            {(frota.data?.itens ?? []).map((veiculo) => (
              <option key={veiculo.id} value={veiculo.id}>
                {identificacaoDoVeiculo(veiculo)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Sinistros da frota"
        // Os mais recentes primeiro (na página carregada) — sinistro velho é
        // processo; o novo é o que precisa de gente.
        ordenacaoInicial={[{ id: 'data', desc: true }]}
        estadoVazio={
          <EmptyState
            icone={ShieldAlert}
            titulo={temFiltro ? 'Nenhum sinistro com esses filtros' : 'Nenhum sinistro registrado'}
            descricao={
              temFiltro
                ? 'Ajuste o status, o tipo ou o veículo para ver outros resultados.'
                : 'Tomara que continue assim. Quando acontecer, registre aqui: BO, seguradora, franquia e — depois da apuração — a responsabilidade do cliente.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirRegistro}>
                  <Plus aria-hidden="true" />
                  Registrar sinistro
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeSinistro
        aberto={dialogoAberto}
        sinistro={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />
    </div>
  )
}
